import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  FaSearch,
  FaTimes,
  FaBoxes,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

// ============================================================
// Module-level session-persistent cache (Fix 1)
// Survives modal closes and re-opens within the same page session.
// ============================================================
const _stockCache = { data: null, totalCount: 0, locationId: null, timestamp: 0 };

// ============================================================
// SearchBar — Debounced search input for stock items
// ============================================================
const SearchBar = memo(({ onSearch, onClear, isLoading, darkMode, placeholder }) => {
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchValue.trim()) {
      debounceRef.current = setTimeout(() => onSearch(searchValue.trim()), 500);
    } else {
      onClear();
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchValue, onSearch, onClear]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <div className="w-full mb-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (searchValue.trim()) onSearch(searchValue.trim()); } }}
            placeholder={placeholder || 'Search stock items by name, brand, category...'}
            className={`w-full pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-300 ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            autoComplete="off"
            autoFocus
          />
          {searchValue && (
            <button onClick={() => { setSearchValue(''); onClear(); inputRef.current?.focus(); }}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors duration-300 ${
                darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}>
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ============================================================
// Fix 5: Memoized stock item card
// Only re-renders when isSelected, item identity, or darkMode changes.
// ============================================================
const StockItemCard = memo(({ item, idx, isSelected, onSelect, setSelectedIndex, darkMode, stock, netStock, showNetStock }) => {
  const totalStock = (stock.freshQuantity || 0) + (stock.damagedQuantity || 0);
  return (
    <div
      onClick={() => onSelect(item)}
      className={`border-2 rounded-xl p-4 hover:border-purple-500 hover:shadow-md transition-all duration-200 cursor-pointer ${
        isSelected
          ? (darkMode ? 'bg-purple-900/30 border-purple-500 ring-2 ring-purple-500/20' : 'bg-purple-50 border-purple-500 ring-2 ring-purple-200')
          : (darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-white')
      }`}
      onMouseEnter={() => setSelectedIndex(idx)}
    >
      {/* Item header */}
      <div className="flex items-center mb-3">
        <div className="w-14 h-14 flex-shrink-0 mr-3">
          <div className={`w-14 h-14 rounded-lg flex items-center justify-center border ${
            darkMode ? 'bg-purple-700 text-purple-200 border-purple-600' : 'bg-purple-100 text-purple-600 border-purple-200'
          }`}>
            <FaBoxes size={20} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm break-words ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            {item.name || 'Unknown'}
          </h4>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {item.category?.name || item.id || 'N/A'}
          </p>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-2">
        {item.id && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {item.id}
          </span>
        )}
        {item.brand?.name && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
            {item.brand.name}
          </span>
        )}
        {item.unitOfMeasure && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {item.unitOfMeasure}
          </span>
        )}
      </div>

      {/* Stock display */}
      <div className="grid grid-cols-2 gap-1 text-xs mb-1">
        <div className={`p-1.5 rounded ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
            Fresh: {stock.freshQuantity || 0}
          </span>
        </div>
        <div className={`p-1.5 rounded ${darkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>
            Damaged: {stock.damagedQuantity || 0}
          </span>
        </div>
      </div>

      {/* Net Stock display (when enabled) */}
      {showNetStock && netStock && (
        <div className={`mt-1 p-2 rounded-lg border ${
          darkMode ? 'bg-cyan-900/20 border-cyan-800/40' : 'bg-cyan-50 border-cyan-200'
        }`}>
          <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
            Net Available Stock
          </p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <span className={darkMode ? 'text-cyan-300' : 'text-cyan-600'}>
              Fresh: {netStock.freshQuantity}
            </span>
            <span className={darkMode ? 'text-orange-300' : 'text-orange-600'}>
              Damaged: {netStock.damagedQuantity}
            </span>
          </div>
        </div>
      )}

      {totalStock === 0 && !showNetStock && (
        <div className="text-xs text-red-500 text-center font-medium mt-1">Out of Stock</div>
      )}

      {isSelected && (
        <div className="mt-2 text-xs text-purple-600 font-medium text-center">
          Press Enter to select
        </div>
      )}
    </div>
  );
}, (prev, next) => prev.isSelected === next.isSelected && prev.item._id === next.item._id && prev.darkMode === next.darkMode);

// ============================================================
// StockItemSelectionModal — Shared across SO, DO, and other pages
// ============================================================
// Props:
//   isOpen          — boolean — show/hide the modal
//   onClose         — () => void
//   onSelect        — (stockItem) => void — callback when an item is selected
//   darkMode        — boolean
//   userId          — string — current user id for API call
//   locationId      — string|null — if provided, can pass location filter + shows location stock
//   filterByLocation— boolean — if true, applies backend location filter when locationId exists
//   showNetStock    — boolean — if true, shows net stock calculation
// ============================================================
const StockItemSelectionModal = ({ isOpen, onClose, onSelect, darkMode, userId, locationId, filterByLocation = true, showNetStock = false }) => {
  const [itemsList, setItemsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(6);
  const searchTermRef = useRef('');

  // Fetch stock items from API
  const fetchItems = useCallback(async (searchTerm = '', { silent = false } = {}) => {
    const locKey = locationId || null;

    // Fix 2: Stale-while-revalidate — show cached data instantly, then silently refresh if stale (>15s)
    if (!searchTerm && !silent && _stockCache.data && _stockCache.locationId === locKey) {
      setItemsList(_stockCache.data);
      setTotalItems(_stockCache.totalCount);
      setPage(1);
      setSelectedIndex(0);
      if (Date.now() - _stockCache.timestamp > 15000) {
        fetchItems('', { silent: true });
      }
      return;
    }

    try {
      if (!silent) setLoading(true);
      searchTermRef.current = searchTerm;
      const queryParams = new URLSearchParams({
        userId: userId || '',
        search: searchTerm || '',
        limit: '50',
        status: 'active'
      });
      if (locationId && filterByLocation) {
        queryParams.set('location', locationId);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const rawItems = Array.isArray(data) ? data : data.data || [];
      const serverTotalCount = data.totalCount || rawItems.length;

      // Strip heavy image data
      const processed = rawItems.map(item => {
        const clean = { ...item };
        if (clean.brand?.image) delete clean.brand.image;
        if (clean.imageData) delete clean.imageData;
        return clean;
      });

      setItemsList(processed);
      setTotalItems(serverTotalCount);

      // Fix 1: Update module-level cache (persists across modal opens within page session)
      if (!searchTerm) {
        _stockCache.data = processed;
        _stockCache.totalCount = serverTotalCount;
        _stockCache.locationId = locKey;
        _stockCache.timestamp = Date.now();
      }

      if (!silent) {
        setPage(1);
        setSelectedIndex(0);
      }
    } catch (error) {
      console.error('StockItemSelectionModal: Error fetching stock items:', error);
      if (!silent) { setItemsList([]); setTotalItems(0); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userId, locationId, filterByLocation]);

  // Fix 3: Prefetch on component mount — data is ready before the user ever opens the modal
  useEffect(() => {
    fetchItems('', { silent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fix 4: Reset page/selection when modal opens (DOM kept alive via CSS hide)
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSelectedIndex(0);
      fetchItems('');
    }
  }, [isOpen, fetchItems]);

  // Auto-refresh every 15 seconds while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      fetchItems(searchTermRef.current, { silent: true });
    }, 15000);
    return () => clearInterval(interval);
  }, [isOpen, fetchItems]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    const startIdx = (page - 1) * perPage;
    const itemsInPage = itemsList.slice(startIdx, startIdx + perPage);
    const count = itemsInPage.length;
    if (count === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex + 1 >= count && page * perPage < itemsList.length) {
        setPage(page + 1);
        setSelectedIndex(0);
      } else {
        setSelectedIndex(Math.min(selectedIndex + 1, count - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex - 1 < 0 && page > 1) {
        setPage(page - 1);
        setSelectedIndex(-1);
      } else {
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const item = itemsInPage[selectedIndex];
      if (item) onSelect(item);
    }
  }, [page, perPage, itemsList, selectedIndex, onClose, onSelect]);

  // Fix -1 index (went to previous page)
  useEffect(() => {
    if (selectedIndex === -1) {
      const startIdx = (page - 1) * perPage;
      const itemsInPage = itemsList.slice(startIdx, startIdx + perPage);
      setSelectedIndex(Math.max(0, itemsInPage.length - 1));
    }
  }, [selectedIndex, page, perPage, itemsList]);

  // Helper: resolve stock for a stock item at the target location
  const getLocationStock = (item) => {
    if (item.locationStock && typeof item.locationStock === 'object') {
      return item.locationStock;
    }
    const locStr = locationId?.toString?.() || locationId;
    if (Array.isArray(item.stock) && item.stock.length > 0 && locationId) {
      const matched = item.stock.find(s => {
        const stockLocId = s.location?._id?.toString?.() || s.location?.toString?.() || s.location;
        return stockLocId?.toString?.() === locStr;
      });
      if (matched) return matched;
    }
    return { freshQuantity: 0, damagedQuantity: 0 };
  };

  // Helper: compute aggregate total stock (across all locations)
  const getAggregateStock = (item) => {
    if (!Array.isArray(item.stock) || item.stock.length === 0) {
      return { freshQuantity: 0, damagedQuantity: 0 };
    }
    return item.stock.reduce((acc, s) => ({
      freshQuantity: acc.freshQuantity + (s.freshQuantity || 0),
      damagedQuantity: acc.damagedQuantity + (s.damagedQuantity || 0)
    }), { freshQuantity: 0, damagedQuantity: 0 });
  };

  // Helper: compute net stock
  // Net = (current stock + ordered + in-transit) - salesOrder
  const getNetStock = (item) => {
    const stock = locationId ? getLocationStock(item) : getAggregateStock(item);
    const ordered = item.quantityOrdered || 0;
    const inTransit = item.inTransitQuantity || 0;
    const soFresh = item.salesOrder?.freshQuantity || 0;
    const soDamaged = item.salesOrder?.damagedQuantity || 0;

    return {
      freshQuantity: (stock.freshQuantity || 0) + ordered + inTransit - soFresh,
      damagedQuantity: (stock.damagedQuantity || 0) - soDamaged
    };
  };

  // Fix 4: CSS hide instead of unmount — DOM stays alive between opens for instant re-display
  if (!isOpen) return null;

  const startIdx = (page - 1) * perPage;
  const itemsInPage = itemsList.slice(startIdx, startIdx + perPage);
  const totalPages = Math.ceil(itemsList.length / perPage);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden m-4 transition-colors duration-300 border-2 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b-2 ${
          darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-100 bg-gradient-to-r from-purple-50 to-violet-50'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
              <FaBoxes className="text-white text-lg" />
            </div>
            <div>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Select Stock Item</h3>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {itemsList.length < totalItems ? `${itemsList.length} of ${totalItems}` : totalItems} items available
                <span className="ml-2 opacity-70">(↑↓ navigate, Enter select, Esc close)</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className={`p-5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <SearchBar
            onSearch={fetchItems}
            onClear={fetchItems}
            isLoading={loading}
            darkMode={darkMode}
            placeholder="Search stock items by name, brand, category..."
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-purple-500 border-t-transparent"></div>
                <div className="absolute inset-0 animate-ping rounded-full h-10 w-10 border border-purple-300 opacity-30"></div>
              </div>
              <p className={`mt-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Searching stock items...</p>
            </div>
          ) : itemsInPage.length === 0 ? (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <FaBoxes className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No stock items found</p>
              {!locationId && locationId !== null && <p className="text-sm text-red-500 mt-2">Please select a location first</p>}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {itemsInPage.map((item, idx) => {
                const stock = locationId ? getLocationStock(item) : getAggregateStock(item);
                const netStock = showNetStock ? getNetStock(item) : null;
                return (
                  <StockItemCard
                    key={item._id}
                    item={item}
                    idx={idx}
                    isSelected={idx === selectedIndex}
                    onSelect={onSelect}
                    setSelectedIndex={setSelectedIndex}
                    darkMode={darkMode}
                    stock={stock}
                    netStock={netStock}
                    showNetStock={showNetStock}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`p-4 border-t-2 flex justify-between items-center ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing {startIdx + 1} to {Math.min(startIdx + perPage, itemsList.length)} of {itemsList.length} items
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPage(p => Math.max(p - 1, 1)); setSelectedIndex(0); }}
                disabled={page === 1}
                className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${
                  darkMode
                    ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700'
                    : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <FaChevronLeft />
              </button>
              <span className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => { setPage(p => Math.min(p + 1, totalPages)); setSelectedIndex(0); }}
                disabled={page >= totalPages}
                className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${
                  darkMode
                    ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700'
                    : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockItemSelectionModal;
